import React, { useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import { obtenerCaiActivo, guardarCai, estadoCai } from '../services/facturasService';
import { mensajeAmigable } from '../utils/errores';
import { fechaLocal } from '../utils/fecha';

// Configuración de la Factura con CAI (SAR). La dueña registra el CAI que le
// autorizó el SAR; al vencerse o agotarse el rango, registra el siguiente.
const VACIO = { cai: '', establecimiento: '000', puntoEmision: '001', rangoInicio: '1', rangoFin: '', fechaLimite: '', siguiente: '' };
const fechaDia = (ymd) => { const [a, m, d] = String(ymd).split('-'); return `${d}/${m}/${a}`; };
const num8 = (n) => String(n).padStart(8, '0');

export default function FacturacionConfig({ datosNegocio }) {
  const { mostrarToast } = useToast();
  const [cai, setCai] = useState(undefined);
  const [form, setForm] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => { obtenerCaiActivo().then(setCai).catch(() => setCai(null)); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const faltanDatos = !String(datosNegocio?.rtn || '').trim() || !String(datosNegocio?.direccion || '').trim();
  const est = cai ? estadoCai(cai) : null;

  const guardar = async () => {
    const caiTxt = form.cai.trim().toUpperCase().replace(/\s+/g, '');
    if (!/^[0-9A-F]{6}(-[0-9A-F]{6}){4}-[0-9A-F]{2}$/.test(caiTxt)) {
      mostrarToast('El CAI debe verse así: XXXXXX-XXXXXX-XXXXXX-XXXXXX-XXXXXX-XX (37 caracteres). Cópialo tal como te lo dio el SAR.', 'error', 7000); return;
    }
    if (!/^\d{3}$/.test(form.establecimiento) || !/^\d{3}$/.test(form.puntoEmision)) {
      mostrarToast('Establecimiento y punto de emisión son de 3 números (ej. 000 y 001).', 'error'); return;
    }
    const ini = parseInt(form.rangoInicio, 10); const fin = parseInt(form.rangoFin, 10);
    const sig = form.siguiente ? parseInt(form.siguiente, 10) : ini;
    if (!(ini >= 1) || !(fin >= ini) || fin > 99999999) { mostrarToast('Revisa el rango: "del" y "al" son los números de factura que te autorizó el SAR.', 'error'); return; }
    if (!(sig >= ini && sig <= fin)) { mostrarToast('El siguiente número a usar debe estar dentro del rango.', 'error'); return; }
    if (!form.fechaLimite) { mostrarToast('Escribe la fecha límite de emisión.', 'error'); return; }
    if (cai && !window.confirm('El CAI actual dejará de usarse y las facturas nuevas saldrán con este CAI. ¿Continuar?')) return;
    setGuardando(true);
    try {
      const nuevo = await guardarCai({ cai: caiTxt, establecimiento: form.establecimiento, puntoEmision: form.puntoEmision,
        rangoInicio: ini, rangoFin: fin, fechaLimite: form.fechaLimite, siguiente: sig });
      setCai(nuevo); setForm(null);
      mostrarToast('✅ CAI registrado. Ya puedes emitir facturas.', 'exito');
    } catch (e) {
      const m = String(e?.message || '');
      mostrarToast(m.includes('CAI_FECHA_PASADA') ? 'La fecha límite ya pasó.'
        : m.includes('CAI_FECHA_MAYOR') ? 'El SAR autoriza máximo 1 año: revisa la fecha límite.'
        : mensajeAmigable(e), 'error', 6000);
    } finally { setGuardando(false); }
  };

  const campo = { width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: '9px', fontSize: '15px', fontFamily: 'inherit', boxSizing: 'border-box' };
  const etq = { display: 'block', fontWeight: 600, fontSize: '13px', margin: '0 0 4px', color: '#374151' };
  const prefijo = form ? `${form.establecimiento || '000'}-${form.puntoEmision || '001'}-01-` : '';

  return (
    <div style={{ marginBottom: '16px', padding: '14px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '12px' }}>
      <div style={{ fontWeight: 700, marginBottom: '4px' }}>🏛️ Factura con CAI (SAR)</div>
      <p style={{ margin: '0 0 10px', fontSize: '12.5px', color: '#4B5563', lineHeight: 1.5 }}>
        Para emitir facturas desde Kontrol, tu negocio debe estar inscrito en el SAR como <b>autoimpresor por sistema computarizado</b> y
        tener un <b>CAI</b> autorizado. Lo solicitas en la Oficina Virtual del SAR o con tu contador; luego copias aquí los datos.
      </p>

      {faltanDatos && (
        <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#B45309', fontWeight: 600 }}>
          ⚠️ Antes de facturar llena tu <b>RTN</b> y tu <b>dirección</b> en "Datos para tus comprobantes de venta" (y guarda).
        </p>
      )}

      {cai === undefined && <p style={{ fontSize: '13px', color: '#6B7280' }}>⏳ Cargando…</p>}

      {cai && !form && (
        <div style={{ background: '#fff', border: '1px solid #D1FAE5', borderRadius: '10px', padding: '10px 12px', fontSize: '13px', lineHeight: 1.7 }}>
          <div><b>CAI:</b> <span style={{ fontFamily: "'DM Mono', monospace" }}>{cai.cai}</span></div>
          <div><b>Rango:</b> {cai.establecimiento}-{cai.punto_emision}-01-{num8(cai.rango_inicio)} al {num8(cai.rango_fin)}</div>
          <div><b>Siguiente factura:</b> {cai.establecimiento}-{cai.punto_emision}-01-{num8(Math.min(cai.siguiente, cai.rango_fin))} · quedan <b>{est.quedan}</b> de {est.total}</div>
          <div><b>Fecha límite de emisión:</b> {fechaDia(cai.fecha_limite)} {est.dias >= 0 ? `(faltan ${est.dias} días)` : ''}</div>
          {!est.puede && <div style={{ color: '#B91C1C', fontWeight: 700 }}>⛔ {est.motivo === 'vencido' ? 'Este CAI ya venció.' : 'Se acabó el rango.'} Solicita un CAI nuevo al SAR y regístralo aquí.</div>}
          {est.aviso === 'por_vencer' && <div style={{ color: '#B45309', fontWeight: 700 }}>⚠️ Tu CAI vence pronto: solicita el siguiente al SAR (se puede pedir en los 2 meses antes).</div>}
          {est.aviso === 'por_agotarse' && <div style={{ color: '#B45309', fontWeight: 700 }}>⚠️ Te quedan pocas facturas en el rango: solicita el siguiente CAI.</div>}
        </div>
      )}

      {cai === null && !form && <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 6px' }}>Todavía no tienes un CAI registrado.</p>}

      {!form && cai !== undefined && (
        <button type="button" onClick={() => setForm({ ...VACIO, establecimiento: cai?.establecimiento || '000', puntoEmision: cai?.punto_emision || '001' })}
          className="k-btn-soft" style={{ marginTop: '10px' }}>{cai ? '➕ Registrar CAI nuevo' : '➕ Registrar mi CAI'}</button>
      )}

      {form && (
        <div style={{ background: '#fff', border: '1px solid #D1FAE5', borderRadius: '10px', padding: '12px', marginTop: '6px' }}>
          <label style={etq}>CAI</label>
          <input value={form.cai} onChange={(e) => set('cai', e.target.value)} disabled={guardando} maxLength={40}
            placeholder="XXXXXX-XXXXXX-XXXXXX-XXXXXX-XXXXXX-XX" style={{ ...campo, fontFamily: "'DM Mono', monospace", marginBottom: '10px' }} />
          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
            <div style={{ flex: 1 }}><label style={etq}>Establecimiento</label>
              <input value={form.establecimiento} inputMode="numeric" maxLength={3} disabled={guardando} onChange={(e) => set('establecimiento', e.target.value.replace(/\D/g, ''))} style={campo} /></div>
            <div style={{ flex: 1 }}><label style={etq}>Punto de emisión</label>
              <input value={form.puntoEmision} inputMode="numeric" maxLength={3} disabled={guardando} onChange={(e) => set('puntoEmision', e.target.value.replace(/\D/g, ''))} style={campo} /></div>
          </div>
          <label style={etq}>Rango autorizado (solo el número final de 8 dígitos)</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
            <span style={{ fontSize: '13px', color: '#6B7280' }}>del</span>
            <input value={form.rangoInicio} inputMode="numeric" maxLength={8} disabled={guardando} onChange={(e) => set('rangoInicio', e.target.value.replace(/\D/g, ''))} style={campo} placeholder="1" />
            <span style={{ fontSize: '13px', color: '#6B7280' }}>al</span>
            <input value={form.rangoFin} inputMode="numeric" maxLength={8} disabled={guardando} onChange={(e) => set('rangoFin', e.target.value.replace(/\D/g, ''))} style={campo} placeholder="500" />
          </div>
          {form.rangoFin && <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#059669' }}>{prefijo}{num8(form.rangoInicio || 1)} al {prefijo}{num8(form.rangoFin)}</p>}
          <label style={etq}>Fecha límite de emisión</label>
          <input type="date" value={form.fechaLimite} min={fechaLocal()} disabled={guardando} onChange={(e) => set('fechaLimite', e.target.value)} style={{ ...campo, marginBottom: '10px' }} />
          <label style={etq}>Siguiente número a usar (opcional)</label>
          <input value={form.siguiente} inputMode="numeric" maxLength={8} disabled={guardando} onChange={(e) => set('siguiente', e.target.value.replace(/\D/g, ''))} style={{ ...campo, marginBottom: '4px' }} placeholder={form.rangoInicio || '1'} />
          <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#6B7280' }}>Solo si ya usaste facturas de este rango en otro lado (ej. talonario). Si no, déjalo vacío.</p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={guardar} disabled={guardando}
              style={{ flex: 1, padding: '11px', background: '#059669', color: '#fff', border: 'none', borderRadius: '9px', fontWeight: 700, cursor: 'pointer' }}>{guardando ? '⏳ Guardando…' : 'Guardar CAI'}</button>
            <button type="button" onClick={() => setForm(null)} disabled={guardando} className="k-btn-soft">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}
